/**
 * OrganizationStatsPanel Component
 * Displays high-level organization metrics
 */
import { OrganizationStats } from '../../api/organizations';
import ProgressBar from '../common/ProgressBar';

interface OrganizationStatsPanelProps {
  stats: OrganizationStats | null;
  loading?: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  subtext?: string;
  showProgress?: boolean;
  progressCurrent?: number;
  progressMax?: number;
  variant?: 'default' | 'warning' | 'danger';
}

function StatCard({
  label,
  value,
  subtext,
  showProgress = false,
  progressCurrent,
  progressMax,
  variant = 'default',
}: StatCardProps) {
  const variantStyles = {
    default: 'bg-dark-bg/30 border-dark-border/50',
    warning: 'bg-yellow-500/10 border-yellow-500/30',
    danger: 'bg-red-500/10 border-red-500/30',
  };

  return (
    <div className={`border rounded-lg p-5 space-y-3 ${variantStyles[variant]}`}>
      <div className="text-xs font-semibold text-dark-text-muted uppercase tracking-wide">
        {label}
      </div>

      <div className="text-3xl font-bold text-dark-text">{value}</div>

      {subtext && (
        <div className="text-xs text-dark-text-muted">{subtext}</div>
      )}

      {showProgress && progressCurrent !== undefined && progressMax !== undefined && (
        <ProgressBar
          current={progressCurrent}
          max={progressMax}
          showValues={false}
          height="sm"
        />
      )}
    </div>
  );
}

export default function OrganizationStatsPanel({
  stats,
  loading = false,
}: OrganizationStatsPanelProps) {
  // Loading skeleton
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-5 h-32 animate-pulse"
          />
        ))}
      </div>
    );
  }

  // Calculate usage percentage
  const usagePercentage = (stats.used_slots / stats.total_slots) * 100;
  const isWarning = usagePercentage >= 80 && usagePercentage < 100;
  const isDanger = usagePercentage >= 100;

  // Determine variant for used slots card
  const usedSlotsVariant: 'default' | 'warning' | 'danger' = isDanger
    ? 'danger'
    : isWarning
    ? 'warning'
    : 'default';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Total slots */}
      <StatCard label="Total Slots" value={stats.total_slots} />

      {/* Used slots with progress bar */}
      <StatCard
        label="Used Slots"
        value={stats.used_slots}
        subtext={`${Math.round(usagePercentage)}% utilized`}
        showProgress
        progressCurrent={stats.used_slots}
        progressMax={stats.total_slots}
        variant={usedSlotsVariant}
      />

      {/* Available slots */}
      <StatCard
        label="Available Slots"
        value={stats.available_slots}
        variant={stats.available_slots === 0 ? 'warning' : 'default'}
      />

      {/* Member count */}
      <StatCard
        label="Member Count"
        value={stats.member_count}
        subtext={`${stats.pending_invitations} pending invite${
          stats.pending_invitations === 1 ? '' : 's'
        }`}
      />
    </div>
  );
}
